/**
 * Catalog panel — public layers shared by anyone on the platform.
 * Lives as a section inside the Layers sidebar tab, below My Layers.
 *
 * Toggling an item adds it to the user's current map view via the same
 * UserLayerManager that powers personal layers. Catalog items aren't
 * "owned" — they don't appear in My Layers, but they show on the map.
 */
import { listCatalog, getLayer } from '../api/layers.js';
import { auth } from '../auth/state.js';
import { toast } from './toast.js';
import { listSkeleton } from './skeleton.js';
import { emptyState, ICON_CATALOG } from './emptyState.js';

const visibleCatalogIds = new Set();
let userLayerManager = null;
let onChangeCallback = null;

export function initCatalogPanel(manager, { onChange } = {}) {
  userLayerManager = manager;
  onChangeCallback = onChange;
  auth.onChange(() => render());
  render();
}

export function getVisibleCatalogIds() {
  return [...visibleCatalogIds];
}

async function render() {
  const host = document.getElementById('catalogHost');
  if (!host) return;

  if (!auth.isAuthenticated()) {
    host.innerHTML = '';
    return;
  }

  // Skeleton during fetch
  host.innerHTML = `<div class="lgroup-title">Catalog</div>${listSkeleton(2)}`;

  let layers = [];
  try { layers = await listCatalog(); }
  catch (e) {
    host.innerHTML = `<div class="lgroup-title">Catalog</div>
      <div class="alert alert-error" style="margin:6px 8px">${escape(e.message)}</div>`;
    return;
  }

  if (layers.length === 0) {
    host.innerHTML = `
      <div class="lgroup-title">Catalog</div>
      ${emptyState({
        icon:  ICON_CATALOG,
        title: 'No published layers',
        body:  'Be the first — share a layer from My layers and it shows up here for the whole platform.',
      })}
    `;
    return;
  }

  // Group by category for editorial structure
  const byCategory = {};
  for (const l of layers) {
    const k = l.category || 'Uncategorised';
    (byCategory[k] ||= []).push(l);
  }

  const groupsHtml = Object.entries(byCategory).map(([cat, items]) => `
    <div class="lgroup-title" style="margin-top:6px">${escape(cat)}</div>
    ${items.map(renderItem).join('')}
  `).join('');

  host.innerHTML = `
    <div class="lgroup-title" style="display:flex;justify-content:space-between;align-items:center">
      <span>Catalog</span>
      <button class="btn-sm btn-outline" data-action="refreshCatalog" style="padding:2px 8px;font-size:10px">Refresh</button>
    </div>
    ${groupsHtml}
  `;
}

function renderItem(l) {
  const isOn = visibleCatalogIds.has(l.id);
  const owner = l.owner_full_name || l.owner_email || `user ${l.owner_id}`;
  const meta = l.layer_type === 'raster'
    ? `${l.band_count ?? '?'} band raster`
    : `${(l.feature_count ?? '–').toLocaleString?.() ?? l.feature_count} features${l.geometry_type ? ' · ' + l.geometry_type : ''}`;
  return `
    <div class="cat-item ${isOn ? 'on' : ''}" data-catalog-layer="${l.id}">
      <div class="cat-name">${escape(l.name)}</div>
      <div class="cat-meta">${escape(meta)}</div>
      <div class="cat-author">by ${escape(owner)}</div>
    </div>
  `;
}

export async function handleCatalogClick(target) {
  const item = target.closest('[data-catalog-layer]');
  if (!item) return false;

  const id = +item.dataset.catalogLayer;
  try {
    if (visibleCatalogIds.has(id)) {
      userLayerManager?.hide(id);
      visibleCatalogIds.delete(id);
    } else {
      const layer = await getLayer(id);
      await userLayerManager.show(layer);
      visibleCatalogIds.add(id);
    }
    await render();
    onChangeCallback?.();
  } catch (e) {
    toast(`Could not toggle catalog layer: ${e.message}`);
  }
  return true;
}

export const refreshCatalog = render;

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}
